import { isPasskeySupported, registerPasskey } from "@src/appV2/Auth/cognito/cognitoWebAuthnAuth";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import { getSsoMode } from "@src/appV2/Auth/RipplingSso/getSsoMode";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useTu } from "@src/appV2/i18n/useTu";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import {
  getPendingRememberEmailPrompt,
  REMEMBER_EMAIL_PROMPT_RESOLVED_EVENT,
} from "@src/appV2/Auth/rememberEmail/rememberEmailStore";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useEffect, useRef, useState } from "react";

import { PasskeySetupPromptDialog } from "./PasskeySetupPromptDialog";
import {
  clearPendingPasskeySetupPrompt,
  hasPendingPasskeySetupPrompt,
  PASSKEY_SETUP_PROMPT_EVENT,
  recordPasskeySetupPromptShown,
  shouldShowPasskeySetupPrompt,
} from "./passkeyPromptStore";

interface Props {
  isFeatureFlagReady: boolean;
  userEmail: string;
  userId: string;
}

export function PasskeySetupPromptManager(props: Props) {
  const { isFeatureFlagReady, userEmail, userId } = props;
  const { tu } = useTu("login");
  const promptModalState = useModalState();
  const { setModalStatus } = promptModalState;
  const hasHandledPrompt = useRef(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const isPasskeyAuthEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_ADMIN_APP_PASSKEY_AUTH, {
    defaultValue: false,
  });

  useEffect(() => {
    function openPromptIfEligible(): void {
      if (
        !isFeatureFlagReady ||
        hasHandledPrompt.current ||
        !hasPendingPasskeySetupPrompt(userId) ||
        getPendingRememberEmailPrompt()
      ) {
        return;
      }
      hasHandledPrompt.current = true;

      if (!isPasskeyAuthEnabled || !isPasskeySupported() || getSsoMode(userEmail) === "sso_only") {
        clearPendingPasskeySetupPrompt();
        return;
      }

      if (!shouldShowPasskeySetupPrompt(userId)) {
        clearPendingPasskeySetupPrompt();
        return;
      }

      recordPasskeySetupPromptShown(userId);
      setModalStatus(ModalStatus.OPEN);
    }

    openPromptIfEligible();
    globalThis.addEventListener(PASSKEY_SETUP_PROMPT_EVENT, openPromptIfEligible);
    globalThis.addEventListener(REMEMBER_EMAIL_PROMPT_RESOLVED_EVENT, openPromptIfEligible);
    return () => {
      globalThis.removeEventListener(PASSKEY_SETUP_PROMPT_EVENT, openPromptIfEligible);
      globalThis.removeEventListener(REMEMBER_EMAIL_PROMPT_RESOLVED_EVENT, openPromptIfEligible);
    };
  }, [isFeatureFlagReady, isPasskeyAuthEnabled, setModalStatus, userEmail, userId]);

  async function handleSetUp() {
    setIsRegistering(true);
    try {
      await registerPasskey();
      logEvent(APP_V2_APP_EVENTS.PASSKEY_REGISTERED);
      showSuccessToast(tu("Passkey added"));
      promptModalState.closeModal();
    } catch (error: unknown) {
      const mapped = mapCognitoError(error, "passkey");
      logError(APP_V2_APP_EVENTS.PASSKEY_REGISTRATION_FAILURE, {
        error,
        metadata: { errorCode: mapped.code },
      });
      showErrorToast(mapped.message);
    } finally {
      setIsRegistering(false);
    }
  }

  return (
    <PasskeySetupPromptDialog
      isLoading={isRegistering}
      modalState={promptModalState}
      onDismiss={promptModalState.closeModal}
      onSetUp={() => {
        void handleSetUp();
      }}
    />
  );
}
